import type { Widget } from '@/lib/manifest/types';

// Runtime guard for the publisher-controlled `widget.embedUrl`. The
// AJV schema enforces `^https://` and the iframe uses
// `sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"`. The combo
// of `allow-same-origin` + an embedUrl that resolves to the vault's own
// origin would give the iframe the vault's cookies, localStorage, and
// parent DOM — same-origin policy treats the iframe as us.
//
// We reject same-origin embedUrls at render time. Operational follow-up:
// host widgets on a dedicated sandbox origin so even cross-origin
// matches can never share vault credentials.

/**
 * Resolve the URL to load a manifest widget in an iframe. Two shapes exist:
 *  - Externally-hosted widgets set an explicit `embedUrl`.
 *  - Agents pushed with `cef push` ship a content-addressed `dag`
 *    ({ bucketId, cid }) + `entry` (file within the uploaded directory),
 *    served by the DDC content gateway at `{cdn}/{bucketId}/{cid}/{entry}`.
 * Returns '' when the widget carries neither (e.g. an unpublished build whose
 * dag has not been stamped yet), so callers can render an empty state.
 */
export function resolveWidgetUrl(
  widget: Widget | null | undefined,
  cdnBaseUrl: string,
): string {
  if (!widget) return '';
  const direct = widget.embedUrl?.trim();
  if (direct) return direct;
  const dag = widget.dag;
  if (dag?.bucketId && dag?.cid) {
    const entry = widget.entry?.trim() || 'index.html';
    const path = entry.replace(/^\/+/, '');
    // `entry` is publisher-controlled; refuse `..` segments so it can't
    // traverse out of the widget's content-addressed dir on the gateway.
    if (path.split('/').includes('..')) return '';
    const base = cdnBaseUrl.replace(/\/+$/, '');
    return `${base}/${dag.bucketId}/${dag.cid}/${path}`;
  }
  return '';
}

export function isSafeWidgetEmbedUrl(embedUrl: string, vaultOrigin: string): boolean {
  let url: URL;
  let host: URL;
  try {
    url = new URL(embedUrl);
    host = new URL(vaultOrigin);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (sameOrigin(url, host)) return false;
  return true;
}

function sameOrigin(a: URL, b: URL): boolean {
  // Origin = protocol + host + port. Use URL.origin so the comparison
  // matches the browser's notion of same-origin — port-aware, case-
  // insensitive on host.
  return a.origin.toLowerCase() === b.origin.toLowerCase();
}
